import { Row, Col, Tooltip, message, Button, Modal, Input, Form } from "antd";
import axios from "axios";
import React, { useState, useEffect } from "react";
import { MODEL, PROMPT } from "../../../custom/GenerateQuesSummaryFormModal";
import set from "lodash/set";

const { TextArea } = Input;

const DEFAULT_STORIES_PROMPT = `Write a concise screenplay-style transcription of the video covering:
  - What unfolds scene by scene (visual/audio narrative)
  - What is being taught and how
  - Key user decision points from the story flow

Output as a single flowing description, not separate sections. Keep it factual and brief and just directly return the transcript. dont include timestamps, etc. just give the details itself.`;

const STORAGE_KEYS = {
    STORIES_PROMPT: "llm_json_stories_prompt",
    QUESTION_PROMPT: "llm_json_question_prompt",
};

const GetLLMJson = (props: any) => {
    const { worksheet_id, worksheet, blocks, publishedBlocksMap } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [storiesPrompt, setStoriesPrompt] = useState(DEFAULT_STORIES_PROMPT);
    const [questionPrompt, setQuestionPrompt] = useState(PROMPT);

    useEffect(() => {
        const savedStoriesPrompt = localStorage.getItem(
            STORAGE_KEYS.STORIES_PROMPT,
        );
        const savedQuestionPrompt = localStorage.getItem(
            STORAGE_KEYS.QUESTION_PROMPT,
        );

        if (savedStoriesPrompt) {
            setStoriesPrompt(savedStoriesPrompt);
        }
        if (savedQuestionPrompt) {
            setQuestionPrompt(savedQuestionPrompt);
        }
    }, []);

    const handleSavePrompts = () => {
        localStorage.setItem(STORAGE_KEYS.STORIES_PROMPT, storiesPrompt);
        localStorage.setItem(STORAGE_KEYS.QUESTION_PROMPT, questionPrompt);
        message.success("Prompts saved successfully");
        setIsModalOpen(false);
    };

    const handleResetPrompts = () => {
        setStoriesPrompt(DEFAULT_STORIES_PROMPT);
        setQuestionPrompt(PROMPT);
        localStorage.removeItem(STORAGE_KEYS.STORIES_PROMPT);
        localStorage.removeItem(STORAGE_KEYS.QUESTION_PROMPT);
        message.success("Prompts reset to defaults");
    };

    const getStorySummary = async (block_id: any) => {
        // First get the stories video and flow from render API
        const renderResponse = await axios
            .request({
                method: "POST",
                url: "https://story-video-service-236148931236.asia-south1.run.app/render",
                data: {
                    storyBlockId: block_id,
                },
            })
            .then(function (response) {
                return response.data;
            })
            .catch(function (error) {
                message.error("Failed to get stories video");
                return null;
            });

        if (!renderResponse?.success) {
            return null;
        }

        // Then get the summary using storiesBlockSummary API
        return axios
            .request({
                method: "POST",
                url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/storiesBlockSummary`,
                data: {
                    data: {
                        sys_prompt: storiesPrompt,
                        worksheet_title: worksheet?.title,
                        stories_video: renderResponse.stories_video,
                        stories_flow: renderResponse.stories_flow,
                    },
                },
            })
            .then(function (response) {
                return response.data?.data?.summary;
            })
            .catch(function (error) {
                message.error("Failed to get story summary");
                return null;
            });
    };

    const getQuesSummary = async (block_id: any, level_id: any) => {
        const context = await axios
            .request({
                method: "POST",
                url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/getLLMContext`,
                data: {
                    data: {
                        worksheet_id: worksheet_id,
                        block_id,
                        level_id,
                    },
                },
            })
            .then(function (response) {
                return response.data?.data;
            });

        return axios
            .request({
                method: "POST",
                url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/quesBlockSummary`,
                data: {
                    data: {
                        sys_prompt: questionPrompt,
                        model: MODEL,
                        worksheet_title: worksheet?.title,
                        context,
                    },
                },
            })
            .then(function (response) {
                return response.data?.data?.summary;
            })
            .catch(function (error) {
                message.error("Failed");
            });
    };

    const handleClick = async (summary = false) => {
        setIsLoading(true);

        const payload = { worksheet_id };

        try {
            const summaries: any = {};
            if (summary) {
                for (const [li, lvl] of Object.entries(blocks ?? [])) {
                    for (const [ci, chunk] of Object.entries(
                        lvl.children ?? [],
                    )) {
                        for (const [bi, block] of Object.entries(
                            chunk.children ?? [],
                        )) {
                            const isAllInOne = block.type === "all_in_one";
                            const isStories =
                                block.type === "stories" ||
                                block.type === "v2_stories";

                            if (!isAllInOne && !isStories) continue;

                            if (block.data?.other?.block_summary) {
                                summaries[publishedBlocksMap[block.id]] =
                                    block.data?.other?.block_summary;
                                continue;
                            }

                            let summary;
                            if (isAllInOne) {
                                summary = await getQuesSummary(
                                    block.id,
                                    lvl.id,
                                );
                            } else if (isStories) {
                                summary = await getStorySummary(block.id);
                            }

                            summaries[publishedBlocksMap[block.id]] = summary;
                            set(
                                blocks,
                                [
                                    li,
                                    "children",
                                    ci,
                                    "children",
                                    bi,
                                    "data",
                                    "other",
                                    "block_summary",
                                ],
                                summary,
                            );
                        }
                    }
                }
            }

            const response = await fetch(
                `${process.env.REACT_APP_WORKSHEET_LLM_API}/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                },
            );

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const { url } = await response.json();

            const fileResponse = await fetch(url);
            const fileData = await fileResponse.json();
            const handleVars = (b) => {
                if (!b.variants?.length) return [];
                return b.variants.map((v) => ({
                    ...v,
                    block: {
                        ...v.block,
                        block_summary:
                            v.block.block_summary ||
                            summaries[v.block.block_id],
                        variants: handleVars(v.block),
                    },
                }));
            };
            const newLvls = fileData.levels.map((l) => ({
                ...l,
                chunks: l.chunks.map((c) => ({
                    ...c,
                    blocks: c.blocks.map((b) => ({
                        ...b,
                        block_summary: b.block_summary || summaries[b.block_id], // summaries must be defined
                        variants: handleVars(b),
                    })),
                })),
            }));
            const blob = new Blob(
                [JSON.stringify({ ...fileData, levels: newLvls }, null, 2)],
                {
                    type: "application/json",
                },
            );
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `worksheet_${worksheet_id}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl); // cleanup
        } catch (err) {
            console.error("Download failed:", err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Row gutter={4}>
                <Col>
                    <Button
                        type="primary"
                        block
                        disabled={isLoading}
                        onClick={() => handleClick()}
                    >
                        {isLoading ? "Loading..." : "Get LLM JSON"}
                    </Button>
                </Col>

                <Col>
                    <Tooltip title={"Get LLM JSON with question block summary"}>
                        <Button
                            type="primary"
                            block
                            disabled={isLoading}
                            onClick={() => handleClick(true)}
                        >
                            {isLoading ? "Loading..." : "Get complete LLM JSON"}
                        </Button>
                    </Tooltip>
                </Col>

                <Col>
                    <Tooltip title={"Edit LLM prompts"}>
                        <Button
                            type="default"
                            disabled={isLoading}
                            onClick={() => setIsModalOpen(true)}
                        >
                            ⚙️
                        </Button>
                    </Tooltip>
                </Col>
            </Row>

            <Modal
                title="Edit LLM Prompts"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                width={800}
                footer={[
                    <Button key="reset" onClick={handleResetPrompts}>
                        Reset to Defaults
                    </Button>,
                    <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSavePrompts}
                    >
                        Save
                    </Button>,
                ]}
            >
                <Form layout="vertical">
                    <Form.Item label="Question Block Prompt">
                        <TextArea
                            value={questionPrompt}
                            onChange={(e) => setQuestionPrompt(e.target.value)}
                            rows={8}
                        />
                    </Form.Item>
                    <Form.Item label="Stories Block Prompt">
                        <TextArea
                            value={storiesPrompt}
                            onChange={(e) => setStoriesPrompt(e.target.value)}
                            rows={6}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default GetLLMJson;
